import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import { useDispatch } from "react-redux";
import {
  fetchWalletBalance,
  fetchAepsBalance,
  fetchSettlementBalance,
} from "../../../Store/Slices/WalletSlice";

const PaymentScreen = ({ mobileNumber, selectedPlan, onBack, operator }) => {
  const [mpin, setMpin] = useState(""); // ✅ MPIN as STRING
  const mpinRefs = useRef([]);
  const [showPopup, setShowPopup] = useState(false);
  const [transactionResponse, setTransactionResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const merchantId = useSelector((state) => state.auth.employeeDetails?.username);
  const account_no = useSelector((state) => state.auth.employeeDetails?.merchant_account_no);

  const { callApi: rechargepay } = useApi("/recharge/transaction/indicore", "POST");

  // Auto focus first MPIN box
  useEffect(() => {
    mpinRefs.current[0]?.focus();
  }, []);

  if (!selectedPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>No Plan Selected</p>
        <button onClick={onBack}>Back</button>
      </div>
    );
  }

  const parseTransactionResponse = (response) => {

    // 🔴 Wallet / API Business Error Case
    if (response?.error_code) {
      return {
        status: "ERROR",
        message: response.message || "Something went wrong",
        txnId: "N/A"
      };
    }

    // ✅ Normal Status Handling
    switch (response.status) {
      case "SUCCESS":
        return { status: "SUCCESS", message: response.customerSummary, txnId: response.txnId };

      case "PENDING":
        return { status: "PENDING", message: "Transaction Pending", txnId: response.txnId };

      case "FAILED":
        return { status: "FAILED", message: response.customerSummary || "Transaction Failed", txnId: response.txnId };

      case "WALLET_DEBIT_FAILED":
        return { status: "FAILED", message: "Wallet debit failed", txnId: response.txnId };

      case "OTP_SENT":
        return { status: "OTP_SENT", message: "OTP sent to registered number", txnId: response.txnId };

      case "INITIATED":
        return { status: "INITIATED", message: "Transaction Initiated", txnId: response.txnId };

      default:
        return { status: "UNKNOWN", message: "Unknown response", txnId: "N/A" };
    }
  };


  // MPIN INPUT HANDLER
  const handleInputChange = (e, index) => {
    const value = e.target.value;
    if (!/^[0-9]?$/.test(value)) return;

    let digits = mpin.split("");
    digits[index] = value;
    const finalMpin = digits.join("");
    setMpin(finalMpin);

    // Move forward
    if (value && index < 5) mpinRefs.current[index + 1]?.focus();

    // Move backward
    if (!value && index > 0) mpinRefs.current[index - 1]?.focus();
  };

  // Handle Transaction
  const handletransaction = async () => {
    if (mpin.length !== 6) {
      alert("Enter 6 digit MPIN");
      return;
    }

    setIsLoading(true);

    const payload = {
      signature: "994AABEB7344ACB79AB67FFB0D74C10642968510A82285F499E946F3A1513175",
      rnum: mobileNumber,
      optr: operator,
      type: "REC",
      mid: merchantId,
      mpin: mpin, // ✅ string
      txn_amount: selectedPlan.rs,
      net_amount: selectedPlan.rs,
      merchant_account_no: account_no,
      add_params: {},
    };

    try {
      const response = await rechargepay(payload);

      const parsed = parseTransactionResponse(response);
      setTransactionResponse(parsed);

    } catch (error) {
      setTransactionResponse({
        status: "FAILED",
        message: error.message || "Server Error",
        txnId: "N/A",
      });
    } finally {
      setIsLoading(false);
      setShowPopup(true);
      dispatch(fetchWalletBalance());
      dispatch(fetchAepsBalance());
      dispatch(fetchSettlementBalance());
    }

  };

  const handlePopupClose = () => {
    setShowPopup(false);
    if (transactionResponse?.status !== "Failed") onBack();
  };

  // ================= POPUP =================
const TransactionPopup = () => {
  if (!showPopup) return null;
  const status = transactionResponse?.status;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">

      <div className="bg-white border rounded-2xl shadow-lg w-full max-w-md p-6">

        {/* HEADER */}
        <h3 className="text-lg font-semibold text-[#1F2A44] text-center mb-4">
          Transaction Status
        </h3>


        {/* DETAILS BOX */}
        <div className="border rounded-lg p-4 text-sm space-y-2 mb-5">

          <div className="flex justify-between">
            <span className="text-gray-500">Status</span>
            <span className="font-semibold">{status}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Message</span>
            <span className="text-right">{transactionResponse?.message}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Txn ID</span>
            <span>{transactionResponse?.txnId}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Amount</span>
            <span>₹{selectedPlan.rs}</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500">Mobile</span>
            <span>{mobileNumber}</span>
          </div>

        </div>


        {/* BUTTON */}
        <button
          onClick={handlePopupClose}
          className="
          w-full py-3 rounded-lg font-semibold
          text-white bg-[#12345A]
          border border-[#12345A]
          hover:bg-white hover:text-[#12345A]
          transition
          "
        >
          OK
        </button>

      </div>

    </div>
  );
};

  // ================= MAIN UI =================
  return (
    <div className="w-full">

      <div className="w-full">

        {/* PAYMENT CARD */}
        <div className="p-6">

          <h3 className="text-lg font-semibold text-[#1F2A44] mb-4">
            Payment Summary
          </h3>

          {/* SUMMARY */}
          <div className="border rounded-xl p-4 text-sm">

            <p className="font-medium text-gray-700">
              Mobile Recharge
            </p>

            <p className="text-gray-500 mt-1">
              +91 {mobileNumber}
            </p>

            <div className="flex justify-between mt-4">
              <span>Recharge Amount</span>
              <span>₹{selectedPlan.rs}</span>
            </div>

            <div className="flex justify-between font-semibold text-lg mt-2">
              <span>Total Payable</span>
              <span>₹{selectedPlan.rs}</span>
            </div>

          </div>


          {/* MPIN */}
          <div className="mt-6">

            <p className="text-sm font-semibold text-gray-700 mb-2">
              Enter MPIN
            </p>

            <div className="flex gap-3">

              {[0, 1, 2, 3, 4, 5].map((i) => (
                <input
                  key={i}
                  ref={(el) => (mpinRefs.current[i] = el)}
                  type="password"
                  maxLength="1"
                  value={mpin[i] || ""}
                  onChange={(e) => handleInputChange(e, i)}
                  className="w-12 h-12 text-center text-lg font-semibold
                border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              ))}

            </div>

          </div>


          {/* BUTTONS */}
          <div className="flex gap-3 mt-8">

            <button
              onClick={onBack}
              className="px-5 py-2 rounded-lg border border-[#12345A] text-[#12345A] 
  hover:bg-[#12345A] hover:text-white transition"
            >
              Back
            </button>

            <button
              onClick={handletransaction}
              disabled={isLoading}
              className="px-6 py-2 rounded-lg font-semibold text-white bg-[#12345A] 
  border border-[#12345A] hover:bg-white hover:text-[#12345A] transition"
            >
              {isLoading ? "Processing..." : "Pay Now"}
            </button>

          </div>

        </div>


        <TransactionPopup />

      </div>

    </div>
  );
};

export default PaymentScreen;
